import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { notifications } from '@mantine/notifications';
import { useHomeTiles, useDeleteHomeTile, useReorderHomeTiles, EditHomeTileModal } from '@modules/home-tiles';
import type { HomeTile } from '@modules/home-tiles';
import { SHOW_HOME_TILES } from '@shared/constants/featureFlags';
import {
  useBanners,
  useDeleteBanner,
  useSetBannerActive,
  useReorderBanners,
  BannerCard,
  StoriesOverlay,
  CreateBannerModal,
  type Banner
} from '@modules/banners';
import { Breadcrumbs } from '@components';
import { CachedImage, DotMenu, DoubleCheckModal, QuietAddTrigger } from '@shared/ui';
import '@shared/styles/page.css';
import './HomePage.css';
import { Loader } from '@mantine/core';

const TILE_TYPE = 'HOME_TILE';
const BANNER_TYPE = 'BANNER';

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const copy = [...arr];
  const [removed] = copy.splice(from, 1);
  copy.splice(to, 0, removed);
  return copy;
}

interface DraggableTileProps {
  tile: HomeTile;
  index: number;
  children: React.ReactNode;
  onMove: (dragIndex: number, dropIndex: number) => void;
}

function DraggableTile({ tile, index, children, onMove }: DraggableTileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: TILE_TYPE,
    item: () => ({ id: tile.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [{ isOver }, drop] = useDrop({
    accept: TILE_TYPE,
    drop: (item: { id: number; index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  drag(drop(ref));
  return (
    <div
      ref={ref}
      className={`home-tile-wrapper ${isDragging ? 'home-tile-dragging' : ''} ${isOver ? 'home-tile-drop-target' : ''}`}
    >
      {children}
    </div>
  );
}

interface DraggableBannerProps {
  banner: Banner;
  index: number;
  children: React.ReactNode;
  onMove: (dragIndex: number, dropIndex: number) => void;
}

function DraggableBanner({ banner, index, children, onMove }: DraggableBannerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: BANNER_TYPE,
    item: () => ({ id: banner.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [{ isOver }, drop] = useDrop({
    accept: BANNER_TYPE,
    drop: (item: { id: number; index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  drag(drop(ref));
  return (
    <div
      ref={ref}
      className={`banner-dnd-wrapper ${isDragging ? 'banner-dragging' : ''} ${isOver ? 'banner-drop-target' : ''}`}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: homeTiles = [], isLoading: isLoadingTiles } = useHomeTiles();
  const { data: banners = [], isLoading: isLoadingBanners } = useBanners(true, false);
  const { mutateAsync: deleteHomeTile, isPending: deleteHomeTilePending } = useDeleteHomeTile();
  const { mutateAsync: deleteBanner, isPending: deleteBannerPending } = useDeleteBanner();
  const setBannerActive = useSetBannerActive();
  const reorderTiles = useReorderHomeTiles();
  const reorderBanners = useReorderBanners();

  const [editingTile, setEditingTile] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [isStoriesOverlayOpen, setIsStoriesOverlayOpen] = useState(false);
  const [isCreateBannerModalOpen, setIsCreateBannerModalOpen] = useState(false);
  const [bannerToEdit, setBannerToEdit] = useState<Banner | null>(null);
  const [isEditBannerModalOpen, setIsEditBannerModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<Banner | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tileToDelete, setTileToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeleteTileModalOpen, setIsDeleteTileModalOpen] = useState(false);

  const handleEdit = (tileId: number) => {
    setEditingTile(tileId);
    setIsEditModalOpen(true);
  };

  const handleDelete = (tileId: number, tileName: string) => {
    setTileToDelete({ id: tileId, name: tileName });
    setIsDeleteTileModalOpen(true);
  };

  const confirmTileDelete = async () => {
    if (!tileToDelete) return;

    await deleteHomeTile(tileToDelete.id);
    setIsDeleteTileModalOpen(false);
    setTileToDelete(null);
  };

  const cancelTileDelete = () => {
    setIsDeleteTileModalOpen(false);
    setTileToDelete(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTile(null);
  };

  const handleBannerClick = (banner: Banner) => {
    // Проверяем, есть ли активные истории
    const hasActiveStories = banner.stories && banner.stories.some(story => story.isActive);

    if (hasActiveStories) {
      // Если есть активные истории - открываем их
      setSelectedBanner(banner);
      setIsStoriesOverlayOpen(true);
    } else if (banner.link) {
      // Если нет историй, но есть путь - переходим по пути
      navigate(banner.link);
    }
    // Если нет ни историй, ни ссылки - ничего не делаем
  };

  const handleBannerEdit = (banner: Banner) => {
    setBannerToEdit(banner);
    setIsEditBannerModalOpen(true);
  };

  const handleBannerDelete = (banner: Banner) => {
    setBannerToDelete(banner);
    setIsDeleteModalOpen(true);
  };

  const confirmBannerDelete = async () => {
    if (!bannerToDelete) return;

    try {
      await deleteBanner(bannerToDelete.id);
      notifications.show({
        title: 'Успешно',
        message: `Баннер "${bannerToDelete.title || 'без названия'}" успешно удален`,
        color: 'green',
      });
      setIsDeleteModalOpen(false);
      setBannerToDelete(null);
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось удалить баннер',
        color: 'red',
      });
    }
  };

  const cancelBannerDelete = () => {
    setIsDeleteModalOpen(false);
    setBannerToDelete(null);
  };

  const handleBannerToggleActive = async (banner: Banner) => {
    try {
      await setBannerActive.mutateAsync({
        id: banner.id,
        data: { isActive: !banner.isActive },
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось изменить статус баннера',
        color: 'red',
      });
    }
  };

  const handleTilesReorder = (dragIndex: number, dropIndex: number) => {
    const reordered = arrayMove(homeTiles, dragIndex, dropIndex);
    queryClient.setQueryData(['home-tiles'] as const, reordered);
    reorderTiles.mutate(reordered.map((t, i) => ({ id: t.id, order: i })));
  };

  const handleBannersReorder = (dragIndex: number, dropIndex: number) => {
    const reordered = arrayMove(banners, dragIndex, dropIndex);
    queryClient.setQueryData(['banners', true, false] as const, reordered);
    reorderBanners.mutate(reordered.map((b, i) => ({ id: b.id, order: i })));
  };

  const currentTile = editingTile ? homeTiles.find(t => t.id === editingTile) : null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="page-container banners-page">
        <Breadcrumbs
          items={[
            { label: 'Главная', path: '/' },
            { label: 'Баннеры' },
          ]}
        />
        <div className="banners-page-head">
          <h1 className="banners-page-title">Баннеры</h1>
          <QuietAddTrigger onClick={() => setIsCreateBannerModalOpen(true)}>
            Добавить баннер
          </QuietAddTrigger>
        </div>

        <div className="banners-page__scroll">
          <div className="content-wrapper banners-page__content-wrapper banners-page__content">
            {/* HomeTiles с главной страницы */}
            {SHOW_HOME_TILES ? (
              <div className="home-tiles-section">
                <h2>Плитки</h2>
                {isLoadingTiles ? (
                  <div className="empty-container">
                    <Loader />
                  </div>
                ) : homeTiles.length > 0 ? (
                  <div className="home-tiles-grid">
                    {homeTiles.map((tile, index) => {
                      const menuItems = [
                        {
                          label: 'Редактировать',
                          onClick: () => handleEdit(tile.id),
                        },
                        {
                          label: 'Удалить',
                          onClick: () => handleDelete(tile.id, tile.name),
                          isDanger: true,
                        },
                      ];

                      return (
                        <DraggableTile key={tile.id} tile={tile} index={index} onMove={handleTilesReorder}>
                          <Link to={tile.path} className="home-tile-card" draggable={false}>
                            {tile.img ? (
                              <div className="home-tile-image">
                                <CachedImage src={tile.img} alt={tile.name} />
                              </div>
                            ) : (
                              <div className="home-tile-placeholder">
                                <span>📦</span>
                              </div>
                            )}
                            <div className="home-tile-content">
                              <h3 className="home-tile-title">{tile.name}</h3>
                            </div>
                          </Link>
                          <div className="home-tile-menu" onClick={(e) => e.stopPropagation()}>
                            <DotMenu items={menuItems} />
                          </div>
                        </DraggableTile>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-container">
                    <p className="empty-message">Нет плиток</p>
                  </div>
                )}
              </div>
            ) : null}

            {/* Баннеры */}
            <section className="banners-card">
              {isLoadingBanners ? (
                <div className="empty-container">
                  <Loader />
                </div>
              ) : banners.length > 0 ? (
                <div className="banners-list">
                  {banners.map((banner, index) => (
                    <DraggableBanner key={banner.id} banner={banner} index={index} onMove={handleBannersReorder}>
                      <BannerCard
                        banner={banner}
                        onOpenStories={handleBannerClick}
                        onEdit={handleBannerEdit}
                        onDelete={handleBannerDelete}
                        onToggleActive={handleBannerToggleActive}
                      />
                    </DraggableBanner>
                  ))}
                </div>
              ) : (
                <div className="empty-container">
                  <p className="empty-message">Нет баннеров</p>
                </div>
              )}
            </section>
          </div>
        </div>

        {currentTile && (
          <EditHomeTileModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            homeTile={currentTile}
          />
        )}

        <StoriesOverlay
          banner={selectedBanner}
          isOpen={isStoriesOverlayOpen}
          onClose={() => {
            setIsStoriesOverlayOpen(false);
            setSelectedBanner(null);
          }}
        />

        <CreateBannerModal
          isOpen={isCreateBannerModalOpen || isEditBannerModalOpen}
          onClose={() => {
            setIsCreateBannerModalOpen(false);
            setIsEditBannerModalOpen(false);
            setBannerToEdit(null);
          }}
          banner={bannerToEdit}
        />

        <DoubleCheckModal
          isOpen={isDeleteModalOpen}
          onClose={cancelBannerDelete}
          title="Удалить баннер?"
          message={bannerToDelete ? `Вы уверены, что хотите удалить баннер "${bannerToDelete.title || 'без названия'}"?` : ''}
          secondButtonText="Удалить"
          secondButtonAction={confirmBannerDelete}
          isLoading={deleteBannerPending}
        />

        <DoubleCheckModal
          isOpen={isDeleteTileModalOpen}
          onClose={cancelTileDelete}
          title="Удалить плитку?"
          message={tileToDelete ? `Вы уверены, что хотите удалить "${tileToDelete.name}" с главной страницы?` : ''}
          secondButtonText="Удалить"
          isLoading={deleteHomeTilePending}
          secondButtonAction={confirmTileDelete}
        />
      </div>
    </DndProvider>
  );
}

